import { Path, rect } from '@geom';
import { LineIndex, Scanner, Token } from '@parser';
import { DEFAULT_LOCALE_ID, iString, IString } from '@strings';
import { expectDefined } from '@utils';
import { isArrowName } from './Arrow';
import { CadetDoc, NodeType, type ElementAttrs } from './CadetDoc';
import { CadetLexer } from './CadetLexer';
import { CadetParser } from './CadetParser';
import {
  AssistantNoteDirective,
  BoundsDirective,
  ClassDirective,
  Directive,
  ElementDirective,
  ImageDirective,
  IncludeDirective,
  NoteDirective,
  PathDirective,
  SettingsDirective,
  StateDirective
} from './Directives';
import { newIdentifier } from './Identifier';
import { UnresolvedImageSource } from './ImagePlaceholder';
import type { Item } from './Item';
import { injectIncludesWithMap } from './prelude/includeExpansion';
import { resolveInclude } from './prelude/IncludeRegistry';
import { validateLocaleValue } from './settingsUtils';
import { EdgeStatement, GroupStatement, Statement, UnitStatement } from './Statements';

export class CadetReader extends Scanner {
  doc: CadetDoc;
  private _nodeIds = new Set<string>();
  private _edgeIds = new Set<string>();
  // Tracks directives seen per element ID
  private _elementDirectiveTracker = new Map<string, Set<string>>();

  constructor(docOrText: CadetDoc | string, textArg?: string) {
    let doc: CadetDoc;
    let text: string;

    // Handle constructor overloading
    if (typeof docOrText === 'string') {
      doc = new CadetDoc();
      text = docOrText;
    } else {
      doc = docOrText;
      text = textArg ?? '';
    }

    // === Phase 0: Pre-parse include expansion ===
    // If this is a fresh string doc, expand declared includes prior to lex/parse.
    // When appending to an existing doc, we do not expand includes here.
    let lexer: CadetLexer;
    if (typeof docOrText === 'string') {
      // Lightweight scan for top-level :INCLUDE directives in both pretty and compact forms.
      // Matches occurrences at line start or after a semicolon on the same line following the prologue.
      // Example compact: "%CADET-1.0; :INCLUDE prelude; A -> B"
      const includeRe = /(^|[;\n\r])\s*:INCLUDE\s+([A-Za-z_][A-Za-z0-9_]*)/g;
      const includes: { symbol: string; text: string }[] = [];
      let m: RegExpExecArray | null;
      while ((m = includeRe.exec(text)) !== null) {
        const symbol = m[2];
        const bundle = resolveInclude(symbol);
        if (bundle && bundle.length > 0) {
          for (const t of bundle) includes.push({ symbol, text: t });
        }
        // Unknown symbols are validated later during directive processing (fail-fast via fatalError)
      }

      if (includes.length > 0) {
        const { text: injectedText, lineIds } = injectIncludesWithMap(text, includes);
        const lineIndex = new LineIndex(injectedText, { lineIds });
        lexer = new CadetLexer(injectedText, { lineIndex });
      } else {
        // No includes present; parse as-is. Missing defaults will error via normal paths.
        lexer = new CadetLexer(text);
      }
    } else {
      // Append into existing doc; do not expand includes here
      lexer = new CadetLexer(text);
    }
    if (lexer.hasErrors) {
      throw expectDefined(lexer.parseError);
    }

    const parser = new CadetParser(lexer);
    if (parser.hasErrors) {
      const error = expectDefined(parser.parseError);
      // console.error(error.toString());
      throw error;
    }

    // Initialize Scanner base class
    super(parser);
    this.doc = doc; // Assign doc instance

    // Base classes are bootstrapped by CadetDoc.Classes; no additional init needed here.

    // Ths contrivance is necessary so we can read the real current `this` in the debugger.
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const ths = this;

    // === Phase 1: Initial Item Separation ===
    // Separate top-level items; nested items remain within GroupStatements for now.
    // We only need top-level statements and class directives at this stage.
    const topLevelClassDirectives: ClassDirective[] = [];
    const topLevelStatements: Statement[] = [];
    for (const item of parser.items) {
      if (item instanceof ClassDirective) {
        topLevelClassDirectives.push(item);
      } else if (item instanceof Statement) {
        topLevelStatements.push(item);
      }
      // Ignore top-level ElementDirectives in this initial separation
    }

    // === Phase 2: Class Definition ===
    // Process directives that declare classes first.
    for (const classDirective of topLevelClassDirectives) {
      ths.classDirective(classDirective);
    }

    // === Phase 3: Statement Processing (Graph Creation) ===
    // Process statements in order, creating the basic graph topology.
    // This recursively handles statements inside groups via groupStatement method.
    for (const statement of topLevelStatements) {
      ths.statement(statement);
    }

    // === Phase 4: Type Resolution ===
    // Resolve node types (Unit vs Group) based on GroupStatement declarations.
    ths.resolveNodeTypes(topLevelStatements);

    // === Phase 5: Hierarchy and Placement ===
    // Process group hierarchy to establish parent-child forest structure and gather references.
    const refs = ths.processGroupHierarchy(topLevelStatements);
    // Place units/groups into the final forest structure based on references.
    ths.processUnitPlacement(refs);

    // === Phase 6: Directive Processing ===
    // Find and process all directives recursively from the AST.

    const getAllDirectives = (items: Item[]): Directive[] => {
      const directives: Directive[] = [];
      for (const item of items) {
        if (item instanceof Directive) {
          directives.push(item);
        } else if (item instanceof GroupStatement) {
          directives.push(...getAllDirectives(item.items));
        }
      }
      return directives;
    };

    // Get ALL directives recursively from the original parse tree structure
    const allDirectives = getAllDirectives(parser.items);

    let settingsDirectiveFound = false; // Flag to ensure only one :SETTINGS
    const includeSymbols: string[] = []; // Collect declared includes in order

    // Process ALL collected directives
    for (const directive of allDirectives) {
      if (directive instanceof IncludeDirective) {
        const symbol = directive.symbol.value;
        includeSymbols.push(symbol);
        // Validate symbol exists in registry; prefer fail-fast
        if (!resolveInclude(symbol)) {
          this.fatalError(`Unknown include symbol: ${symbol}`, directive.start ?? 0);
        }
      } else if (directive instanceof SettingsDirective) {
        if (settingsDirectiveFound) {
          this.warning(
            'Multiple :SETTINGS directives found. Only one is allowed (using last one found).',
            directive.start ?? 0
          );
        }
        settingsDirectiveFound = true;
        // Store the raw parsed object into CadetDoc.rawSettings
        // Validation will happen later during translation to the main Doc object.
        this.doc.rawSettings = directive.value.rawValue as Record<string, unknown>; // Store the raw JS object
      } else if (directive instanceof ImageDirective) {
        // Store image origin metadata in the document. No I/O here.
        const imageId = directive.imageId.value;
        const directiveStart = expectDefined(directive.start);
        const raw = directive.value.value;
        // Heuristics: detect origin kind
        let origin:
          | { type: 'data'; value: string }
          | { type: 'url'; value: string }
          | { type: 'file'; value: string }
          | { type: 'svg'; value: string } = { type: 'svg', value: raw };
        if (/^data:image\//i.test(raw)) {
          origin = { type: 'data', value: raw };
        } else if (/^https?:\/\//i.test(raw)) {
          origin = { type: 'url', value: raw };
        } else if (/\n|<svg\b/i.test(raw) || /<[^>]+>/.test(raw)) {
          origin = { type: 'svg', value: raw };
        } else {
          // Treat as file path by default (relative or absolute)
          origin = { type: 'file', value: raw };
        }
        // Enforce uniqueness: image IDs must not collide with existing element IDs or images
        if (
          this._nodeIds.has(imageId) ||
          this._edgeIds.has(imageId) ||
          this.doc.hasImage(imageId)
        ) {
          this.fatalError(
            `Duplicate element ID: "${imageId}" is already used.`,
            directiveStart
          );
        }
        const placeholder = new UnresolvedImageSource(imageId, origin);
        this.doc.setImage(imageId, placeholder);
      } else if (directive instanceof ElementDirective) {
        // Keep existing processing for element-specific directives
        this.elementDirective(directive);
      }
      // ClassDirectives were handled earlier.
    }
    // === End Phase 6 ===

    // Persist declared includes on the doc; order preserved
    if (includeSymbols.length > 0) {
      this.doc.includes.push(...includeSymbols);
    }

    // === Phase 7: Settings Validation (no mutation besides default omission) ===
    if (this.doc.rawSettings) {
      const localeVal = (this.doc.rawSettings as { locale?: unknown }).locale;
      validateLocaleValue(localeVal, msg => this.fatalError(msg, 0));
      if (localeVal === (DEFAULT_LOCALE_ID as string)) {
        // Omit default (remove key); keep rest untouched.
        const { rawSettings } = this.doc;
        if (Object.prototype.hasOwnProperty.call(rawSettings, 'locale')) {
          const copy = { ...rawSettings };
          delete copy.locale;
          this.doc.rawSettings = Object.keys(copy).length === 0 ? undefined : copy;
        }
      }
    }

    // Final error check
    if (ths.hasErrors) {
      throw expectDefined(ths.parseError);
    }
  }

  statement(statement: Statement): void {
    if (statement instanceof UnitStatement) {
      this.unitStatement(statement);
    } else if (statement instanceof EdgeStatement) {
      this.edgeStatement(statement);
    } else if (statement instanceof GroupStatement) {
      this.groupStatement(statement);
    } else {
      throw new Error(`Unknown statement type: ${statement.constructor.name}`);
    }
  }

  unitStatement(statement: UnitStatement): void {
    const doc = this.doc;
    const id = expectDefined(statement.id);
    const titleRaw = statement.title?.value as unknown;
    const title =
      titleRaw instanceof IString
        ? titleRaw
        : typeof titleRaw === 'string' && titleRaw.length > 0
          ? iString(titleRaw)
          : undefined;
    const statementStart = expectDefined(statement.start);
    const clsToken = statement.cls;

    // Get the class name from the token
    let className: string;

    if (clsToken) {
      // Use the semantic value (without prefix) from the token
      className = clsToken.value;
      const clsStart = expectDefined(clsToken.start);

      // --- Initial Class Validation (Keep as non-fatal for now) ---
      // If the class doesn't exist or isn't a Unit type, we can't proceed
      // meaningfully with this statement, so record error and return.
      if (!doc.hasClass(className)) {
        this.error(`Class not found: ${clsToken.text}`, clsStart);
        return; // Cannot proceed without a valid class
      }
      if (!doc.isClassKindOf(className, 'Unit')) {
        this.error(`Class ${clsToken.text} is not Unit or a subclass of Unit`, clsStart);
        return; // Cannot proceed if class is wrong base type
      }
      // --- End Initial Class Validation ---
    } else {
      // If no class is specified, use "Unit" as default
      className = 'Unit';
    }

    // If the node doesn't exist, create it
    if (!doc.hasNode(id)) {
      // Check for ID collision across all elements (Node or Edge)
      if (this._nodeIds.has(id) || this._edgeIds.has(id)) {
        // This is a fatal error - ID already claimed
        this.fatalError(`Duplicate element ID: "${id}" is already used.`, statementStart);
      }
      // Add new node
      if (title) {
        doc.addNode(id, className, title);
      } else {
        // Manually add node without title attribute
        doc.graph.addNode(id, {
          data: { cls: className, attrs: {}, type: NodeType.UNIT }
        });
      }
      // Track the newly claimed ID
      this._nodeIds.add(id);
      return; // Successfully created node, done with this statement
    }

    // --- Node already exists - Validate the re-declaration ---

    // If the existing node is a group, we cannot re-declare it as a unit.
    if (doc.isGroup(id)) {
      // This is a fatal type conflict.
      this.fatalError(
        `Node ${id} is not a unit: already declared to be a group.`,
        statementStart
      );
      // fatalError throws, so no return needed.
    }

    // If a title is declared in the current statement...
    {
      const incoming = title ? title.get() : '';
      if (incoming !== '') {
        const existingTitle = doc.nodeTitle(id);
        const existingTitleString = existingTitle.get();
        if (existingTitleString === '') {
          if (title) doc.setNodeTitle(id, title);
        } else if (existingTitleString === incoming) {
          this.warning(
            `Node ${id} already has the same title: ${existingTitleString}`,
            statementStart
          );
        } else {
          this.fatalError(
            `Node ${id} already has a different title: ${existingTitleString}`,
            statementStart
          );
        }
      }
    }

    // If a non-default class is declared in the current statement...
    if (className !== 'Unit' && clsToken) {
      const existingClass = doc.nodeClass(id);

      if (existingClass === className) {
        // If the class is identical, just issue a warning.
        this.warning(
          `Node ${id} already has the same class: ${clsToken.text}`,
          statementStart
        );
      } else if (doc.isClassKindOf(className, existingClass)) {
        // If the new class is a subclass of the existing one (refinement), update it.
        doc.setNodeClass(id, className);
      } else if (doc.isClassKindOf(existingClass, className)) {
        // If the existing class is already more specific than the new one, do nothing.
        // This is valid, no warning needed.
      } else {
        // If the classes are unrelated (e.g., trying to change /Action to /Goal),
        // this is considered a fatal conflict.
        this.fatalError(
          `Node ${id} already has an unrelated class: ${existingClass}`,
          statementStart
        );
        // fatalError throws, so no return needed.
      }
    }
    // If execution reaches here, the re-declaration was valid or only generated warnings.
  }

  edgeStatement(edge: EdgeStatement): void {
    const doc = this.doc;
    const statementStart = expectDefined(edge.start);

    // --- Process Source Node ---
    const sourceStatement = edge.source;
    const sourceId = expectDefined(sourceStatement.id);
    const sourceTitleRaw = sourceStatement.title?.value as unknown;
    const sourceTitle =
      sourceTitleRaw instanceof IString
        ? sourceTitleRaw
        : iString((sourceTitleRaw as string | undefined) ?? '');
    const sourceClsToken = sourceStatement.cls;
    const sourceClassName = sourceClsToken ? sourceClsToken.value : 'Unit';
    const sourceClsStart = expectDefined(sourceClsToken?.start ?? statementStart);
    this.processNodeForEdge(
      sourceId,
      sourceClassName,
      sourceTitle,
      sourceClsStart,
      statementStart,
      sourceClsToken
    );
    // If processNodeForEdge recorded *any* error (fatal or non-fatal), stop processing this edge statement.
    if (this.hasErrors) {
      return;
    }

    // --- Process Target Node ---
    const targetStatement = edge.target;
    const targetId = expectDefined(targetStatement.id);
    const targetTitleRaw = targetStatement.title?.value as unknown;
    const targetTitle =
      targetTitleRaw instanceof IString
        ? targetTitleRaw
        : iString((targetTitleRaw as string | undefined) ?? '');
    const targetClsToken = targetStatement.cls;
    const targetClassName = targetClsToken ? targetClsToken.value : 'Unit';
    const targetClsStart = expectDefined(targetClsToken?.start ?? statementStart);
    this.processNodeForEdge(
      targetId,
      targetClassName,
      targetTitle,
      targetClsStart,
      statementStart,
      targetClsToken
    );
    // <<< ADD ESLint disable comment for the next line >>>
    // If processing target node caused errors, stop. Check is needed as processNodeForEdge can add errors.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (this.hasErrors) {
      return;
    }

    // --- Get Edge Class ---
    // Use non-fatal errors and return for invalid class definitions
    if (!edge.cls) {
      this.error('Internal Error: Edge class token missing.', statementStart);
      return;
    }
    const edgeClassName = edge.cls.value;
    const edgeClsStart = expectDefined(edge.cls.start);
    if (!doc.hasClass(edgeClassName)) {
      this.error(`Edge class not found: ${edge.cls.text}`, edgeClsStart);
      return;
    }
    if (!doc.isClassKindOf(edgeClassName, 'Edge')) {
      this.error(
        `Class ${edge.cls.text} is not Edge or a subclass of Edge`,
        edgeClsStart
      );
      return;
    }
    // --- End Edge Class ---

    // --- Determine and Validate Edge ID ---
    const edgeId = edge.id ?? newIdentifier('e', doc.graph.edges);

    // Check for collision with *any* previously defined node or edge ID
    if (this._nodeIds.has(edgeId) || this._edgeIds.has(edgeId)) {
      // Fatal: ID already claimed by any element type
      this.fatalError(
        `Duplicate element ID: "${edgeId}" is already used.`,
        statementStart
      );
    }

    // Check if graph already has edge (catches same-type duplicates)
    if (doc.hasEdge(edgeId)) {
      // Fatal: Graph module prevents duplicate edges with same ID
      this.fatalError(
        `Edge with ID ${edgeId} already exists (Graph Error or Duplicate).`,
        statementStart
      );
    }
    // --- End Edge ID Validation ---

    // --- Add Edge ---
    doc.graph.addEdge(edgeId, sourceId, targetId, {
      cls: edgeClassName,
      attrs: {}
    });
    // Add the newly claimed ID to the edge tracker
    this._edgeIds.add(edgeId);
    // --- End Add Edge ---
  }

  /**
   * Helper method to process a node referenced as an endpoint in an edge statement.
   * Creates the node if it doesn't exist, validates it if it does.
   * Enforces ID uniqueness across both nodes and edges.
   * Records non-fatal errors for conflicting re-declarations (title, class).
   * Throws fatal errors for cross-type ID conflicts.
   *
   * @param id - The node identifier
   * @param className - The node's class name (from edge stmt or default 'Unit')
   * @param title - The node's title (if provided in edge stmt)
   * @param clsStart - Position of the class token for error reporting
   * @param statementStart - Position of the statement for error reporting
   * @param clsToken - The optional class token for better error messages
   */
  private processNodeForEdge(
    id: string,
    className: string,
    title: IString,
    clsStart: number,
    statementStart: number,
    clsToken?: Token<string>
  ): void {
    const doc = this.doc;

    // --- Initial Class Validation ---
    // Use non-fatal errors and return, as we can't proceed if the class is invalid.
    if (className !== 'Unit' && className !== 'Group' && !doc.hasClass(className)) {
      const errorText = clsToken ? clsToken.text : className;
      this.error(`Class not found: ${errorText}`, clsStart);
      return;
    }
    const isUnitClass = className === 'Unit' || doc.isClassKindOf(className, 'Unit');
    const isGroupClass = className === 'Group' || doc.isClassKindOf(className, 'Group');
    if (!isUnitClass && !isGroupClass) {
      const errorText = clsToken ? clsToken.text : className;
      this.error(
        `Class ${errorText} is not a valid node class (Unit, Group, or their subclasses)`,
        clsStart
      );
      return;
    }
    // --- End Initial Class Validation ---

    const impliedNodeType = isGroupClass ? NodeType.GROUP : NodeType.UNIT;

    if (!doc.hasNode(id)) {
      // --- Node Doesn't Exist Yet ---
      // Check for collision with *any* previously defined node or edge ID
      if (this._nodeIds.has(id) || this._edgeIds.has(id)) {
        // Fatal: ID already claimed by any element type
        this.fatalError(`Duplicate element ID: "${id}" is already used.`, statementStart);
      }
      // Create the node
      doc.addNode(id, className, title, impliedNodeType);
      // Add the newly claimed ID to the node tracker
      this._nodeIds.add(id);
    } else {
      // --- Node Already Exists ---
      // Check if this existing node's ID conflicts with a previously claimed *edge* ID.
      if (this._edgeIds.has(id)) {
        // Fatal: Node ID clashes with an existing Edge ID
        this.fatalError(
          `ID conflict: Node "${id}" (referenced in edge) has an ID that is already used by an edge.`,
          statementStart
        );
      }

      // Validate/update existing node based on info in edge statement (non-fatal errors)
      const incomingTitleStr = title.get();
      if (incomingTitleStr !== '') {
        const existingTitle = doc.nodeTitle(id);
        const existingTitleStr = existingTitle.get();
        if (existingTitleStr === '') {
          doc.setNodeTitle(id, title);
        } else if (existingTitleStr !== incomingTitleStr) {
          this.error(
            `Node ${id} referenced in edge has a different title: ${existingTitleStr}`,
            statementStart
          );
        }
      }
      if (className !== 'Unit' && className !== 'Group') {
        const existingCls = doc.nodeClass(id);
        if (existingCls !== className) {
          if (doc.isClassKindOf(className, existingCls)) {
            doc.setNodeClass(id, className);
          } else if (!doc.isClassKindOf(existingCls, className)) {
            this.error(
              `Node ${id} referenced in edge has an unrelated class: ${existingCls}`,
              statementStart
            );
          }
        }
      }
    }
  }

  groupStatement(group: GroupStatement): void {
    // Process all statements contained within the group
    for (const item of group.items) {
      if (item instanceof Statement) {
        this.statement(item);
      }
    }
  }

  /**
   * Processes the statement hierarchy to establish parent-child relationships
   * between groups and collect node references for placement calculation.
   *
   * Phase 1 of containment processing: Build the group forest structure and
   * gather all node references, distinguishing authoritative (Unit/Group)
   * from non-authoritative (Edge) references.
   *
   * @returns A structure containing two maps: authoritativeRefs and edgeRefs.
   */
  private processGroupHierarchy(statements: Statement[]): {
    authoritativeRefs: Map<string, Set<string | undefined>>; // Allow undefined
    edgeRefs: Map<string, Set<string | undefined>>; // Allow undefined
  } {
    // Change map types
    const authoritativeRefs = new Map<string, Set<string | undefined>>();
    const edgeRefs = new Map<string, Set<string | undefined>>();

    // Modify addRef helper
    const addRef = (
      map: Map<string, Set<string | undefined>>, // Allow undefined
      nodeId: string,
      groupId?: string // Undefined for root-level references
    ): void => {
      if (!map.has(nodeId)) {
        map.set(nodeId, new Set());
      }
      const groups = expectDefined(map.get(nodeId));
      groups.add(groupId); // Add groupId, which might be undefined
    };

    // Recursive function to process statements and collect refs
    const processStatementsRecursive = (
      stmts: Statement[],
      currentGroupId?: string
    ): void => {
      for (const stmt of stmts) {
        if (stmt instanceof UnitStatement) {
          const unitId = expectDefined(stmt.id);
          addRef(authoritativeRefs, unitId, currentGroupId);
        } else if (stmt instanceof EdgeStatement) {
          const sourceId = expectDefined(stmt.source.id);
          const targetId = expectDefined(stmt.target.id);
          addRef(edgeRefs, sourceId, currentGroupId);
          addRef(edgeRefs, targetId, currentGroupId);
          // Also add authoritative refs if nodes are declared inline with class/title
          if (stmt.source.cls || stmt.source.title) {
            addRef(authoritativeRefs, sourceId, currentGroupId);
          }
          if (stmt.target.cls || stmt.target.title) {
            addRef(authoritativeRefs, targetId, currentGroupId);
          }
        } else if (stmt instanceof GroupStatement) {
          // Process the group node itself first
          const groupId = this.processGroupNode(stmt, currentGroupId); // Pass parent ID
          // Add authoritative reference for the group node itself to its parent
          addRef(authoritativeRefs, groupId, currentGroupId);

          // Recursively process items inside the group
          // Filter items to pass only Statements, fixing the type error
          const childStatements = stmt.items.filter(
            (item): item is Statement => item instanceof Statement
          );
          processStatementsRecursive(childStatements, groupId); // Pass filtered array
        }
      }
    };

    // Start processing from top-level statements
    processStatementsRecursive(statements, undefined); // Root level

    // Ensure all nodes from the graph are present in at least one map,
    // associating them with the root if they have no other references.
    for (const nodeId of this.doc.graph.nodes) {
      if (!authoritativeRefs.has(nodeId) && !edgeRefs.has(nodeId)) {
        // Node exists but wasn't referenced anywhere? Add to authoritativeRefs with no group (root)
        authoritativeRefs.set(nodeId, new Set());
      } else {
        // Ensure node exists in authoritativeRefs if it only has edgeRefs
        if (!authoritativeRefs.has(nodeId) && edgeRefs.has(nodeId)) {
          authoritativeRefs.set(nodeId, new Set()); // Add with empty set initially
        }
        // Ensure node exists in edgeRefs if it only has authoritativeRefs
        if (authoritativeRefs.has(nodeId) && !edgeRefs.has(nodeId)) {
          edgeRefs.set(nodeId, new Set()); // Add with empty set initially
        }
      }
    }

    return { authoritativeRefs, edgeRefs };
  }

  /**
   * Processes a GroupStatement, creating or updating the corresponding group node
   * in the document's graph and forest. Establishes parent-child relationship.
   * Does NOT recursively process contained items here (handled by caller).
   *
   * @param group - The GroupStatement to process.
   * @param parentId - The ID of the parent group (optional, for nested groups).
   * @returns The ID of the processed or created group node.
   */
  private processGroupNode(group: GroupStatement, parentId?: string): string {
    // Return the group ID
    const doc = this.doc;
    const statementStart = expectDefined(group.start);

    // Use provided ID or generate one deterministically
    const groupId = group.id ?? newIdentifier('g', doc.graph.nodes);

    // Process group attributes
    const groupTitleRaw = group.title?.value as unknown;
    const title =
      groupTitleRaw instanceof IString
        ? groupTitleRaw
        : typeof groupTitleRaw === 'string' && groupTitleRaw.length > 0
          ? iString(groupTitleRaw)
          : undefined;
    const className = group.cls?.value ?? 'Group';
    const clsToken = group.cls;

    // Ensure class exists and is a Group class
    if (!doc.hasClass(className)) {
      const displayName = clsToken ? clsToken.text : className;
      this.error(`Class not found: ${displayName}`, statementStart);
      return groupId; // Return ID even on error
    }
    if (!doc.isClassKindOf(className, 'Group')) {
      const displayName = clsToken ? clsToken.text : className;
      this.error(
        `Class ${displayName} is not Group or a subclass of Group`,
        statementStart
      );
      return groupId; // Return ID even on error
    }

    // Create group node attributes object (used in both branches below)
    const attrs: ElementAttrs = {};
    if (title) attrs.title = title;

    // Check if the node already exists in the graph
    if (doc.hasNode(groupId)) {
      // Node exists path - logic for potentially updating an existing node

      // Check if this ID, which exists as a node, was *also* claimed by an edge earlier.
      if (this._edgeIds.has(groupId)) {
        this.fatalError(
          `ID conflict: Node "${groupId}" cannot be defined as a group because the ID is already used by an edge.`,
          statementStart
        );
      }

      let classNeedsUpdate = false;
      // Ensure type is GROUP (it might have been created as UNIT previously)
      if (doc.isUnit(groupId)) {
        doc.updateNodeType(groupId, NodeType.GROUP);
        // Type changed, class might be wrong (e.g., it might still be 'Unit')
        classNeedsUpdate = true;
      }

      // Ensure class is appropriate for a GROUP node
      const existingCls = doc.nodeClass(groupId);
      // The class derived from the GroupStatement (e.g., 'Group' or '/MyGroup')
      const desiredCls = className; // className is 'Group' or custom from group stmt

      if (classNeedsUpdate && !doc.isClassKindOf(existingCls, 'Group')) {
        // If type just changed AND existing class is NOT a group class (e.g., it's 'Unit'),
        // forcefully set it to the desired class ('Group' or custom group class like '/MyGroup').
        doc.setNodeClass(groupId, desiredCls);
      } else if (desiredCls !== 'Group' && desiredCls !== existingCls) {
        // If a custom group class was specified in the GroupStatement (e.g., G1: /MyGroupClass),
        // update only if it's more specific than existing, OR if existing is not a valid Group class.
        // (Don't downgrade a specific group class like /MyProcess to the generic 'Group').
        if (
          doc.isClassKindOf(desiredCls, existingCls) ||
          !doc.isClassKindOf(existingCls, 'Group')
        ) {
          doc.setNodeClass(groupId, desiredCls);
        }
      } else if (existingCls !== 'Group' && !doc.isClassKindOf(existingCls, 'Group')) {
        // Catch all: If existing class is still not a group class (e.g. 'Unit'),
        // and no specific custom class was provided (desiredCls == 'Group'),
        // set it to the default 'Group'.
        doc.setNodeClass(groupId, 'Group');
      }
      // Note: If desiredCls is 'Group' and existingCls is already a valid Group class (like /MyProcess), we don't change it.

      // Update title if provided and not already set
      if (title && !doc.hasNodeTitle(groupId)) {
        doc.setNodeTitle(groupId, title);
      }

      // Check if parent relationship needs setting/updating
      const currentParent = doc.graph.forest.parent(groupId);
      if (currentParent !== parentId) {
        if (parentId !== undefined && !doc.graph.hasNode(parentId)) {
          // Record error but continue processing node attributes if possible
          this.error(
            `Parent group ${parentId} not found for group ${groupId}`,
            statementStart
          );
        } else {
          if (parentId !== undefined) {
            // Move node under new parent
            doc.graph.forest.moveNode(groupId, {
              newParent: parentId,
              newEdge: newIdentifier()
            });
          } else {
            // Move node to root (if it currently has a parent)
            if (currentParent !== undefined) {
              // This requires Forest API change or workaround
              console.warn(
                `CadetReader: Moving node ${groupId} to root requires Forest API review.`
              );
              // Potential workaround:
              // const edgeId = doc.graph.forest.inEdge(groupId);
              // if (edgeId) { doc.graph.forest.removeEdge(edgeId); }
            }
          }
        }
      }
      // If the node already existed, its ID was already added to _elementIds
      // when it was first created. No need to add it again here.
    } else {
      // Node doesn't exist path - logic for adding a new node

      if (this._nodeIds.has(groupId) || this._edgeIds.has(groupId)) {
        this.fatalError(
          `Duplicate element ID: "${groupId}" is already used.`,
          statementStart
        );
      }

      // Add node to the graph with NodeType.GROUP and correct parentage
      doc.graph.addNode(groupId, {
        data: { cls: className, attrs, type: NodeType.GROUP },
        ...(parentId !== undefined && {
          parent: parentId,
          newEdge: newIdentifier()
        })
      });

      // Add successfully created group ID to the tracker
      this._nodeIds.add(groupId);
    }

    // Return the group ID that was processed or created
    return groupId;
  }

  /**
   * Resolves node types by identifying nodes that were initially created as units
   * but are actually groups based on group statements. This is necessary to handle
   * forward references to groups in edge statements.
   *
   * @param statements - The array of parsed statements
   */
  private resolveNodeTypes(statements: Statement[]): void {
    const doc = this.doc;

    // Identify all group IDs from group statements
    const groupIds = new Set<string>();
    for (const stmt of statements) {
      if (stmt instanceof GroupStatement && stmt.id) {
        groupIds.add(stmt.id);
      }
    }

    // Resolve any ambiguous nodes
    for (const nodeId of doc.graph.nodes) {
      // If node appears in group statements, it's a GROUP
      if (groupIds.has(nodeId)) {
        // Update node type if it was previously created as a UNIT
        if (doc.isUnit(nodeId)) {
          doc.updateNodeType(nodeId, NodeType.GROUP);
        }
      }
    }
  }

  /**
   * Phase 2 of group containment processing: Places nodes (units and groups)
   * into the forest hierarchy based on prioritized references.
   * Prioritizes Authoritative references, uses Edge references as fallback.
   * If a node is referenced at the root level (outside any group), it remains at the root.
   *
   * @param refs - An object containing authoritativeRefs and edgeRefs maps.
   * The sets within maps can contain string group IDs or undefined for root refs.
   */
  private processUnitPlacement(refs: {
    authoritativeRefs: Map<string, Set<string | undefined>>;
    edgeRefs: Map<string, Set<string | undefined>>;
  }): void {
    const doc = this.doc;
    const forest = doc.graph.forest;
    const { authoritativeRefs, edgeRefs } = refs;

    // Combine all node IDs from both maps
    const allNodeIds = new Set([...authoritativeRefs.keys(), ...edgeRefs.keys()]);

    for (const nodeId of allNodeIds) {
      if (!doc.graph.hasNode(nodeId)) continue;

      let finalPlacementRefs: Set<string | undefined> | undefined = undefined;

      // Determine which set of references to use (prioritize authoritative)
      const authRefsSet = authoritativeRefs.get(nodeId);
      if (authRefsSet && authRefsSet.size > 0) {
        finalPlacementRefs = authRefsSet;
      } else {
        const edgRefsSet = edgeRefs.get(nodeId);
        if (edgRefsSet && edgRefsSet.size > 0) {
          finalPlacementRefs = edgRefsSet;
        }
        // else: No references, node stays wherever it was initially (likely root)
      }

      // Calculate target parent ID based on the chosen reference set
      let targetParentId: string | undefined = undefined; // Default to root

      if (finalPlacementRefs && finalPlacementRefs.size > 0) {
        const groupsOnly = new Set<string>();
        let hasRootRef = false;

        // Separate root (undefined) references from actual group IDs
        for (const id of finalPlacementRefs) {
          if (id === undefined) {
            hasRootRef = true;
          } else {
            groupsOnly.add(id);
          }
        }

        // *** Placement Rule: Root reference takes precedence ***
        if (hasRootRef) {
          targetParentId = undefined; // Place at root
        } else if (groupsOnly.size === 1) {
          targetParentId = Array.from(groupsOnly)[0]; // Single group reference
        } else if (groupsOnly.size > 1) {
          // Multiple group references, calculate LCA of those groups
          targetParentId = forest.leastCommonAncestor(groupsOnly);
          if (targetParentId === undefined) {
            // console.warn(`CadetReader: LCA returned undefined for node ${nodeId} refs [${Array.from(groupsOnly).join(', ')}]. Placing at root.`);
            targetParentId = undefined;
          }
        }
        // else: groupsOnly is empty and no root ref -> no references -> stays root
      }

      // Get the current parent and move the node if necessary
      const currentParentId = forest.parent(nodeId);
      if (currentParentId !== targetParentId) {
        // Check if the target parent group exists before moving
        if (targetParentId !== undefined && !doc.graph.hasNode(targetParentId)) {
          this.error(
            `Target parent group ${targetParentId} for node ${nodeId} not found in graph.`,
            0
          );
        } else {
          if (targetParentId !== undefined) {
            // Move under the target parent
            forest.moveNode(nodeId, {
              newParent: targetParentId, // Guaranteed string
              newEdge: newIdentifier()
            });
          } else {
            // Move to root (if it currently has a parent)
            if (currentParentId !== undefined) {
              // TODO: Replace console.warn with actual mechanism to move to root
              // if Forest API is updated or workaround is implemented.
              console.warn(
                `CadetReader: Moving node ${nodeId} to root is not directly supported by the current moveNode signature. Review Forest API.`
              );
              // Example workaround (needs Forest API support):
              // const edgeId = doc.graph.forest.inEdge(nodeId);
              // if (edgeId) { doc.graph.forest.removeEdge(edgeId); }
            }
          }
        }
      }
    } // end for loop over allNodeIds
  }

  classDirective(directive: ClassDirective): void {
    const doc = this.doc;
    const directiveStart = expectDefined(directive.start);
    // Detect include-origin via LineIndex line IDs: inc-<symbol>-<n>
    const lineInfo = this.lineIndex.offsetToLineInfo(directiveStart);
    const lineId = lineInfo.id ?? '';
    let includeOrigin: string | undefined;
    if (lineId.startsWith('inc-')) {
      const m = /^inc-([^-]+)-/.exec(lineId);
      if (m) includeOrigin = m[1];
    }

    // Get the class name and superclass name from token values (without prefix)
    const className = directive.cls.value;
    const superClassName = directive.superCls?.value;

    // For validation, we need display names (with prefix/syntax)
    const displayName = directive.cls.text;

    // For EDGE directives, the class can be an arrow or start with /
    // For UNIT and GROUP directives, the class must start with /
    if (
      directive.name === ':EDGE' &&
      !displayName.startsWith('/') &&
      !isArrowName(displayName)
    ) {
      this.error(
        `Invalid edge class name: ${displayName}. Edge class names must start with / or be a valid arrow.`,
        directiveStart
      );
      return;
    } else if (
      (directive.name === ':UNIT' || directive.name === ':GROUP') &&
      !displayName.startsWith('/')
    ) {
      this.error(
        `Invalid class name: ${displayName}. Unit and group class names must start with /.`,
        directiveStart
      );
      return;
    }

    // Determine default superclass based on directive type
    let defaultSuperClass: string;
    if (directive.name === ':UNIT') {
      defaultSuperClass = 'Unit';
    } else if (directive.name === ':GROUP') {
      defaultSuperClass = 'Group';
    } else if (directive.name === ':EDGE') {
      defaultSuperClass = 'Edge';
    } else {
      this.error(`Unknown class directive type: ${directive.name}`, directiveStart);
      return;
    }

    // Use provided superclass or default
    const parentClassName = superClassName ?? defaultSuperClass;
    const superClassDisplay = directive.superCls?.text ?? parentClassName;

    // Validate that superclass exists
    if (!doc.hasClass(parentClassName)) {
      this.error(
        `Superclass not found: ${superClassDisplay}`,
        directive.superCls?.start ?? directiveStart
      );
      return;
    }

    // Validate that the superclass is appropriate for this class type
    if (directive.name === ':UNIT' && !doc.isClassKindOf(parentClassName, 'Unit')) {
      this.error(
        `Superclass ${superClassDisplay} for UNIT class ${displayName} must be a subclass of Unit`,
        directiveStart
      );
      return;
    } else if (
      directive.name === ':GROUP' &&
      !doc.isClassKindOf(parentClassName, 'Group')
    ) {
      this.error(
        `Superclass ${superClassDisplay} for GROUP class ${displayName} must be a subclass of Group`,
        directiveStart
      );
      return;
    } else if (
      directive.name === ':EDGE' &&
      !doc.isClassKindOf(parentClassName, 'Edge')
    ) {
      this.error(
        `Superclass ${superClassDisplay} for EDGE class ${displayName} must be a subclass of Edge`,
        directiveStart
      );
      return;
    }

    // Check if class already exists
    if (doc.hasClass(className)) {
      this.error(`Class already defined: ${displayName}`, directiveStart);
      return;
    }

    // The ObjectValue already contains parsed attributes as JavaScript objects
    const attrs = directive.value.value;

    // Add the class to the document using semantic values (without prefix)
    doc.classes.addClass(className, parentClassName, { attrs });
    if (includeOrigin) {
      doc.includeClassOrigins.set(className, includeOrigin);
    }
  }

  elementDirective(directive: ElementDirective): void {
    const doc = this.doc;
    const elementId = directive.id.value;
    const directiveName = directive.name; // e.g., ":NOTE", ":BOUNDS", ":STATE"
    const directiveStart = expectDefined(directive.start);

    // 1. Validate element exists
    const isNode = doc.hasNode(elementId);
    const isEdge = doc.hasEdge(elementId);
    if (!isNode && !isEdge) {
      // Use error, likely parsing should stop if target doesn't exist
      this.error(
        `Element with ID "${elementId}" not found for ${directiveName}`,
        directiveStart
      );
      return;
    }

    // 2. Enforce Singleton Rule for *all* Element Directives (non-fatal error)
    if (!this._elementDirectiveTracker.has(elementId)) {
      // First time seeing *any* directive for this element, initialize its set
      this._elementDirectiveTracker.set(elementId, new Set<string>());
    }
    const directivesSeenForElement = expectDefined(
      this._elementDirectiveTracker.get(elementId)
    );

    if (directivesSeenForElement.has(directiveName)) {
      // Duplicate detected! Record non-fatal error. Processing continues (last one wins).
      this.error(
        `Multiple ${directiveName} directives found for element '${elementId}'. Using last definition encountered.`,
        directiveStart
      );
    } else {
      // First time seeing *this type* of directive for this element, record it in the tracker.
      directivesSeenForElement.add(directiveName);
    }

    // 3. Process Specific Directive Type
    if (directive instanceof NoteDirective) {
      const noteValue = directive.noteText.value as unknown;
      const noteText =
        noteValue instanceof IString ? noteValue : iString(noteValue as string);
      if (isNode) doc.setNodeNote(elementId, noteText);
      else doc.setEdgeNote(elementId, noteText);
    } else if (directive instanceof AssistantNoteDirective) {
      const noteValue2 = directive.noteText.value as unknown;
      const noteText =
        noteValue2 instanceof IString ? noteValue2 : iString(noteValue2 as string);
      if (isNode) doc.setNodeAssistantNote(elementId, noteText);
      else doc.setEdgeAssistantNote(elementId, noteText);
    } else if (directive instanceof BoundsDirective) {
      if (!isNode) {
        this.error(
          `BOUNDS directive cannot apply to edge '${elementId}'`,
          directiveStart
        );
        return;
      }
      const boundsText = directive.bounds.value;
      const parts = boundsText.trim().split(/\s+/);
      if (parts.length !== 4 || parts.some(p => isNaN(Number(p)))) {
        this.error(
          `Invalid bounds format "${boundsText}". Expected "x y width height"`,
          directiveStart
        );
        return;
      }
      const [x, y, width, height] = parts.map(Number);
      const bounds = rect(x, y, x + width, y + height);
      doc.setNodeBounds(elementId, bounds);
    } else if (directive instanceof PathDirective) {
      if (!isEdge) {
        this.error(`PATH directive cannot apply to node '${elementId}'`, directiveStart);
        return;
      }
      const pathData = directive.path.value;
      try {
        const pathObj = Path.fromSvgPathData(pathData);
        doc.setEdgePath(elementId, pathObj);
      } catch (e) {
        const errorSuffix = e instanceof Error ? e.message : String(e);
        this.error(
          `Invalid path data "${pathData}" for PATH directive: ${errorSuffix}`,
          directiveStart
        );
      }
    } else if (directive instanceof StateDirective) {
      const stateAttrs = directive.value.rawValue as Record<string, unknown>;
      // Define reserved keys not settable via :STATE (cls handled separately, bounds/path stored elsewhere)
      const reservedKeys = ['title', 'note', 'assistantNote'];

      for (const [key, value] of Object.entries(stateAttrs)) {
        if (reservedKeys.includes(key)) {
          // Attempts to set reserved keys via :STATE
          this.error(
            `Attribute "${key}" cannot be set via :STATE directive for element '${elementId}'. Use specific syntax or directives (e.g., "title", :NOTE).`,
            directiveStart
          );
          continue; // Skip this key-value pair
        }
        // Apply the valid state attribute to the element's instance attrs map
        if (isNode) {
          doc.setNodeAttr(elementId, key, value);
        } else {
          // isEdge must be true if isNode is false (due to check at start)
          doc.setEdgeAttr(elementId, key, value);
        }
      }
    } else {
      // Should not happen if all ElementDirective subclasses are handled
      this.error(
        `Unknown element directive type: ${directive.constructor.name}`,
        directiveStart
      );
    }
  }
}
